import { FC, useCallback } from 'react';
import { Form, Field } from 'react-final-form';
import Button from 'components/Button';
import {
  FileUploadField,
  InputField,
  KeywordsField,
  PlainSelectField,
  TextareaField,
} from 'components/fields';
import { Campaign, CampaignType } from 'models/Campaign';
import { ScrapeSource } from 'constants/campaign';
import useCampaignSettings from 'hooks/useCampaignSettings';

import './CampaignForm.styles.scss';

type CampaignFormProps = {
  initialValues: Partial<CampaignFormType>;
  onSubmit: (values: CampaignFormType) => void;
  submitInProgress: boolean;
  mode: 'edit' | 'create';
};

export type CampaignFormType<T extends CampaignType = CampaignType> =
  Campaign & {
    type: T;
  } & (T extends CampaignType.Personal
      ? { file: File } // only Personal campaigns have file
      : { file?: never });

const scrapeSourceOptions = Object.values(ScrapeSource).map((value) => ({
  label: value.charAt(0).toUpperCase() + value.slice(1),
  value,
}));

const required = (value: any) => (value ? undefined : 'Required');

const CampaignForm: FC<CampaignFormProps> = ({
  initialValues,
  onSubmit,
  submitInProgress,
  mode,
}) => {
  const { data: campaignSettings } = useCampaignSettings();

  const createHandleTypeChange = useCallback(
    (form: any) => (value: CampaignType) => {
      if (mode !== 'create') return;
      form.change(
        'queryPrompt',
        value === CampaignType.Scraping
          ? campaignSettings?.queryPrompt
          : campaignSettings?.personalCampaignPrompt,
      );
    },
    [campaignSettings, mode],
  );

  return (
    <Form<CampaignFormType>
      onSubmit={onSubmit}
      initialValues={{ type: CampaignType.Scraping, ...initialValues }}
      render={({ handleSubmit, values, form }) => {
        const handleTypeChange = createHandleTypeChange(form);

        return (
          <form onSubmit={handleSubmit} className="campaign-form">
            <Field
              name="name"
              label="Title"
              component={InputField}
              placeholder="Title"
              validate={required}
            />

            <Field
              name="type"
              label="Campaign type"
              component={PlainSelectField}
              placeholder="Campaign type"
              validate={required}
              onChange={handleTypeChange}
              options={Object.values(CampaignType).map((type) => ({
                label: type.charAt(0).toUpperCase() + type.slice(1),
                value: type,
              }))}
            />

            {values.type !== CampaignType.Personal && (
              <Field
                name="scrapeKeywords"
                label="Keywords"
                component={KeywordsField}
              />
            )}

            <Field
              name="launchLimit"
              label="Launch limit"
              component={InputField}
              type="number"
              step={1}
            />

            <Field
              name="queryPrompt"
              label="Campaign prompt"
              placeholder="Enter your campaign prompt here..."
              component={TextareaField}
              rows={12}
            />

            {values.type === CampaignType.Personal && (
              <Field
                name="file"
                label="Import File"
                component={FileUploadField}
                accept={{ 'text/csv': ['.csv'] }}
              />
            )}

            <Button
              type="submit"
              className="submit-button"
              isLoading={submitInProgress}
            >
              Save changes
            </Button>
          </form>
        );
      }}
    />
  );
};

export default CampaignForm;
